<template>
  <div class="md-layout products-list-page">
    <div class="md-layout-item">
      <md-card class="products-card">

        <!-- ── Header ── -->
        <md-card-header class="card-header">
          <div class="header-left">
            <div class="card-icon">
              <md-icon>local_mall</md-icon>
            </div>
            <div class="header-titles">
              <h4 class="title">Wingio Products</h4>
              <span class="subtitle">{{ total }} product{{ total !== 1 ? 's' : '' }} total</span>
            </div>
          </div>
          <md-button
            class="md-raised md-primary add-btn"
            @click="$router.push({ path: 'wingio-product-create' })"
          >
            <md-icon>add</md-icon>
            Add Product
          </md-button>
        </md-card-header>

        <!-- ── Filters Bar ── -->
        <div class="filters-bar">
          <div class="filter-group">
            <md-icon class="filter-icon">search</md-icon>
            <input
              v-model="filters.search"
              class="filter-input"
              placeholder="Search by name…"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-chips">
            <button
              v-for="type in typeOptions"
              :key="type.value"
              class="chip"
              :class="{ active: filters.productType === type.value }"
              @click="setTypeFilter(type.value)"
            >
              <span class="chip-dot" :style="{ background: type.color }"></span>
              {{ type.label }}
            </button>
          </div>

          <div class="filter-group filter-perpage">
            <label>Show</label>
            <select v-model.number="pagination.perPage" class="filter-select" @change="pagination.currentPage = 1">
              <option v-for="n in [10, 25, 50, 100]" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>

        <!-- ── Table ── -->
        <md-card-content class="table-content">
          <div v-if="loading" class="loading-state">
            <md-progress-spinner md-mode="indeterminate" :md-diameter="40" :md-stroke="3" />
            <p>Loading products…</p>
          </div>

          <div v-else-if="queriedData.length === 0" class="empty-state">
            <md-icon class="empty-icon">inventory_2</md-icon>
            <p>No products found</p>
            <small>Try adjusting your filters or add a new product.</small>
          </div>

          <table v-else class="products-table">
            <thead>
              <tr>
                <th class="col-name sortable" @click="sort('name')">
                  Name
                  <md-icon class="sort-icon">{{ sortIcon('name') }}</md-icon>
                </th>
                <th class="col-display sortable" @click="sort('displayName')">
                  Display Name
                  <md-icon class="sort-icon">{{ sortIcon('displayName') }}</md-icon>
                </th>
                <th class="col-type">Type</th>
                <th class="col-amount">Amount</th>
                <th class="col-date sortable" @click="sort('created_at')">
                  Created
                  <md-icon class="sort-icon">{{ sortIcon('created_at') }}</md-icon>
                </th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in queriedData"
                :key="item._id"
                class="product-row"
                :class="{ 'row-accessories': item.productType === 'Accessories' }"
              >
                <td class="col-name">
                  <div class="name-cell">
                    <div class="name-avatar" :style="{ background: typeColor(item.productType) }">
                      {{ (item.name || '?')[0].toUpperCase() }}
                    </div>
                    <span>{{ item.name }}</span>
                  </div>
                </td>
                <td class="col-display text-muted">{{ item.displayName }}</td>
                <td class="col-type">
                  <span class="type-badge" :class="typeBadgeClass(item.productType)">
                    {{ item.productType || '—' }}
                  </span>
                </td>
                <td class="col-amount">
                  <template v-if="item.stripePriceDetail">
                    <strong>{{ getCurrencySymbol(item.stripePriceDetail.currency) }}{{ (item.stripePriceDetail.unit_amount / 100).toFixed(2) }}</strong>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="col-date text-muted small">{{ HumanReadableDateFormat(item.created_at) }}</td>
                <td class="col-actions">
                  <div class="actions-row">
                    <!-- Prices – not for accessories -->
                    <md-button
                      class="md-just-icon md-simple action-btn btn-info"
                      title="Prices"
                      @click="$router.push({ path: 'wingio-product-prices', query: { id: item._id } })"
                    >
                      <md-icon>currency_pound</md-icon>
                    </md-button>

                    <!-- Shipping – not for accessories/subscription -->
                    <!--<md-button
                      v-if="item.productType !== 'Accessories' && item.productType !== 'Subscription'"
                      class="md-just-icon md-simple action-btn btn-info"
                      title="Shipping Prices"
                      @click="$router.push({ path: 'wingio-product-shipping-prices', query: { id: item._id } })"
                    >
                      <md-icon>local_shipping</md-icon>
                    </md-button>-->

                    <!-- Edit -->
                    <md-button
                      v-if="item.productType !== 'Subscription'"
                      class="md-just-icon md-simple action-btn btn-warning"
                      title="Edit"
                      @click="$router.push({ path: 'wingio-product-edit', query: { id: item._id } })"
                    >
                      <md-icon>edit</md-icon>
                    </md-button>

                    <!-- Images (standard) / Colour variants (accessories) -->
                    <md-button
                      v-if="item.productType !== 'Subscription'"
                      class="md-just-icon md-simple action-btn btn-purple"
                      :title="item.productType === 'Accessories' ? 'Colour Variants' : 'Images'"
                      @click="$router.push({ path: 'wingio-product-images', query: { id: item._id } })"
                    >
                      <md-icon>{{ item.productType === 'Accessories' ? 'palette' : 'photo_library' }}</md-icon>
                    </md-button>

                    <!-- Delete -->
                    <md-button
                      v-if="item.productType !== 'Subscription'"
                      class="md-just-icon md-simple action-btn btn-danger"
                      title="Delete"
                      @click="deleteProduct(item._id)"
                    >
                      <md-icon>delete_outline</md-icon>
                    </md-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </md-card-content>

        <!-- ── Pagination ── -->
        <md-card-actions class="pagination-bar">
          <span class="pagination-info">
            Showing {{ total === 0 ? 0 : from + 1 }}–{{ to }} of {{ total }}
          </span>
          <div class="pagination-controls">
            <md-button class="md-just-icon md-simple" :disabled="pagination.currentPage <= 1" @click="pagination.currentPage--">
              <md-icon>chevron_left</md-icon>
            </md-button>
            <span class="page-indicator">{{ pagination.currentPage }} / {{ totalPages }}</span>
            <md-button class="md-just-icon md-simple" :disabled="pagination.currentPage >= totalPages" @click="pagination.currentPage++">
              <md-icon>chevron_right</md-icon>
            </md-button>
          </div>
        </md-card-actions>

      </md-card>
    </div>
  </div>
</template>

<script>
import OrdersDataService from '../../services/SuperAdmin/Kids/OrdersDataService';
import Swal from 'sweetalert2';

export default {
  data() {
    return {
      loading: false,
      tableData: [],
      currentSort: 'created_at',
      currentSortOrder: 'desc',
      filters: {
        search: '',
        productType: 'all',
      },
      pagination: {
        perPage: 25,
        currentPage: 1,
      },
      typeOptions: [
        { label: 'All', value: 'all', color: '#9e9e9e' },
        { label: 'Onetime', value: 'Onetime', color: '#4caf50' },
        { label: 'Subscription', value: 'Subscription', color: '#2196f3' },
        { label: 'Lifetime', value: 'Lifetime Subscription', color: '#ff9800' },
        { label: 'Accessories', value: 'Accessories', color: '#9c27b0' },
      ],
    };
  },

  computed: {
    filteredData() {
      let data = [...this.tableData];
      if (this.filters.productType !== 'all') {
        data = data.filter((d) => d.productType === this.filters.productType);
      }
      if (this.filters.search.trim()) {
        const q = this.filters.search.trim().toLowerCase();
        data = data.filter((d) =>
          (d.name || '').toLowerCase().includes(q) ||
          (d.displayName || '').toLowerCase().includes(q)
        );
      }
      return data;
    },
    sortedData() {
      return [...this.filteredData].sort((a, b) => {
        const key = this.currentSort;
        const aVal = a[key] || '';
        const bVal = b[key] || '';
        const cmp = String(aVal).localeCompare(String(bVal));
        return this.currentSortOrder === 'asc' ? cmp : -cmp;
      });
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to() {
      return Math.min(this.from + this.pagination.perPage, this.total);
    },
    total() {
      return this.filteredData.length;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pagination.perPage));
    },
    queriedData() {
      return this.sortedData.slice(this.from, this.to);
    },
  },

  methods: {
    onFilterChange() {
      this.pagination.currentPage = 1;
    },
    setTypeFilter(val) {
      this.filters.productType = val;
      this.pagination.currentPage = 1;
    },
    sort(key) {
      if (this.currentSort === key) {
        this.currentSortOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSort = key;
        this.currentSortOrder = 'asc';
      }
    },
    sortIcon(key) {
      if (this.currentSort !== key) return 'unfold_more';
      return this.currentSortOrder === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
    },
    typeColor(type) {
      const map = {
        Onetime: '#4caf50',
        Subscription: '#2196f3',
        'Lifetime Subscription': '#ff9800',
        Accessories: '#9c27b0',
      };
      return map[type] || '#9e9e9e';
    },
    typeBadgeClass(type) {
      return {
        'badge-onetime': type === 'Onetime',
        'badge-subscription': type === 'Subscription',
        'badge-lifetime': type === 'Lifetime Subscription',
        'badge-accessories': type === 'Accessories',
      };
    },
    getCurrencySymbol(currency) {
      const map = { usd: '$', eur: '€', gbp: '£', inr: '₹', jpy: '¥', aud: 'A$', cad: 'C$', cny: '¥', krw: '₩' };
      return map[(currency || '').toLowerCase()] || (currency || '').toUpperCase();
    },
    HumanReadableDateFormat(date) {
      if (!date) return '—';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    getWingioProducts() {
      this.loading = true;
      OrdersDataService.wingioProductsList()
        .then((response) => {
          this.tableData = response.data.data;
        })
        .catch((e) => {
          this.$toast.error(e.response?.data?.respones_msg || 'Failed to load products');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteProduct(id) {
      Swal.fire({
        title: 'Delete product?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f44336',
        cancelButtonColor: '#9e9e9e',
        confirmButtonText: 'Yes, delete',
      }).then((result) => {
        if (result.value) {
          OrdersDataService.deleteWingioProduct({ id })
            .then((response) => {
              this.$toast.success(response.data.respones_msg);
              this.getWingioProducts();
            })
            .catch((e) => {
              this.$toast.error(e.response?.data?.respones_msg || 'Delete failed');
            });
        }
      });
    },
  },

  beforeMount() {
    this.getWingioProducts();
  },
};
</script>

<style scoped>
.products-list-page { font-family: 'Segoe UI', sans-serif; }

/* Card */
.products-card { border-radius: 12px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.08); }

/* Header */
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%);
  color: white;
}
.header-left { display: flex; align-items: center; gap: 14px; }
.card-icon {
  width: 44px; height: 44px;
  background: rgba(255,255,255,0.2);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.card-icon .md-icon { color: white !important; }
.header-titles .title { color: white; margin: 0; font-size: 18px; font-weight: 600; }
.header-titles .subtitle { color: rgba(255,255,255,0.75); font-size: 13px; }
.add-btn { background: rgba(255,255,255,0.18) !important; color: white !important; border-radius: 8px; border: 1px solid rgba(255,255,255,0.3); }
.add-btn:hover { background: rgba(255,255,255,0.3) !important; }

/* Filters */
.filters-bar {
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  padding: 14px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8ecef;
}
.filter-group { display: flex; align-items: center; gap: 8px; }
.filter-icon { color: #9e9e9e !important; font-size: 20px !important; }
.filter-input {
  border: 1px solid #ddd; border-radius: 8px;
  padding: 7px 12px; font-size: 14px;
  outline: none; width: 220px;
  transition: border-color 0.2s;
}
.filter-input:focus { border-color: #43a047; }
.filter-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.chip {
  display: flex; align-items: center; gap: 5px;
  padding: 5px 12px; border-radius: 20px;
  border: 1px solid #ddd; background: white;
  font-size: 12px; font-weight: 500; cursor: pointer;
  transition: all 0.15s;
}
.chip:hover { border-color: #43a047; color: #43a047; }
.chip.active { background: #43a047; color: white; border-color: #43a047; }
.chip.active .chip-dot { opacity: 0.8; }
.chip-dot { width: 8px; height: 8px; border-radius: 50%; }
.filter-perpage label { font-size: 13px; color: #666; }
.filter-select {
  border: 1px solid #ddd; border-radius: 6px;
  padding: 5px 8px; font-size: 13px; outline: none;
}

/* Table */
.table-content { padding: 0; }
.products-table { width: 100%; border-collapse: collapse; }
.products-table thead tr { background: #f5f5f5; }
.products-table th {
  padding: 12px 16px; font-size: 12px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.5px;
  color: #666; text-align: left; white-space: nowrap;
  border-bottom: 2px solid #e0e0e0;
}
.products-table th.sortable { cursor: pointer; user-select: none; }
.products-table th.sortable:hover { color: #43a047; }
.sort-icon { font-size: 16px !important; vertical-align: middle; color: #bbb !important; }
.products-table td { padding: 12px 16px; font-size: 14px; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.product-row:hover td { background: #f9fffe; }
.product-row:last-child td { border-bottom: none; }
.row-accessories td { background: #fdf6ff; }
.row-accessories:hover td { background: #f8eeff; }

/* Name cell */
.name-cell { display: flex; align-items: center; gap: 10px; }
.name-avatar {
  width: 34px; height: 34px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 14px; font-weight: 700;
  flex-shrink: 0;
}
.text-muted { color: #888; }
.small { font-size: 12px; }

/* Badges */
.type-badge {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px;
}
.badge-onetime { background: #e8f5e9; color: #2e7d32; }
.badge-subscription { background: #e3f2fd; color: #1565c0; }
.badge-lifetime { background: #fff3e0; color: #e65100; }
.badge-accessories { background: #f3e5f5; color: #6a1b9a; }

/* Actions */
.actions-row { display: flex; gap: 2px; align-items: center; }
.action-btn.md-just-icon { width: 32px; height: 32px; min-width: 32px; border-radius: 8px !important; }
.action-btn.md-just-icon .md-icon { font-size: 17px !important; }
.action-btn.btn-info .md-icon { color: #1976d2 !important; }
.action-btn.btn-warning .md-icon { color: #f57c00 !important; }
.action-btn.btn-purple .md-icon { color: #7b1fa2 !important; }
.action-btn.btn-danger .md-icon { color: #c62828 !important; }
.action-btn:hover { background: rgba(0,0,0,0.06) !important; }

/* Empty / loading */
.loading-state, .empty-state {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 60px 20px; color: #aaa; text-align: center;
}
.empty-icon { font-size: 48px !important; margin-bottom: 12px; color: #ccc !important; }
.loading-state p, .empty-state p { margin: 8px 0 4px; font-size: 15px; }
.empty-state small { font-size: 13px; }

/* Pagination */
.pagination-bar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 24px; border-top: 1px solid #f0f0f0;
}
.pagination-info { font-size: 13px; color: #888; }
.pagination-controls { display: flex; align-items: center; gap: 4px; }
.page-indicator { font-size: 13px; color: #555; padding: 0 8px; }
</style>